import { OCRSpaceProvider } from './providers/OCRSpaceProvider.js';
import { supabase } from '../../lib/supabase.js';

/**
 * OCRService — Single point of entry for all OCR operations.
 *
 * The ERP MUST always use this service. Never call a provider directly.
 * To change provider: update getActiveProvider() — nothing else changes.
 *
 * Flow:
 *   File (PDF/image) → OCRService → Provider → Raw text → AIService → Structured data
 *
 * OCRService returns ONLY text. It never interprets products.
 */

const CONFIG_TABLE = 'tienda.erp_config';
const CONFIG_KEY = 'ocr_config';

// In-memory cache for config (refreshed on each wizard open)
let _cachedConfig = null;

/**
 * Load OCR configuration from Supabase (tienda.erp_config).
 * La API Key ya NO se necesita en el frontend — está en Supabase Secrets.
 * @returns {Promise<OCRConfig>}
 */
export async function loadOCRConfig() {
  try {
    const { data, error } = await supabase?.from('erp_config')?.select('value')?.eq('key', CONFIG_KEY)?.maybeSingle();

    if (!error && data?.value) {
      _cachedConfig = data?.value;
      return data?.value;
    }
  } catch {
    // Fall through to defaults
  }

  // Default config — sin API key (está en Supabase Secrets)
  _cachedConfig = {
    provider: 'ocr_space',
    api_key: '', // No se usa — la Edge Function usa Supabase Secrets
    timeout: 60000,
    language: 'spa',
  };
  return _cachedConfig;
}

/**
 * Save OCR configuration to Supabase.
 * @param {OCRConfig} config
 */
export async function saveOCRConfig(config) {
  const { error } = await supabase?.from('erp_config')?.upsert({ key: CONFIG_KEY, value: config, updated_at: new Date()?.toISOString() }, { onConflict: 'key' });

  if (error) throw new Error(`Error guardando configuración OCR: ${error.message}`);
  _cachedConfig = config;
}

/**
 * Get the active OCR provider instance based on current config.
 * @param {OCRConfig} [config] - Optional override config
 * @returns {import('./interfaces/OCRProvider.js').OCRProvider}
 */
export function getActiveProvider(config) {
  const cfg = config || _cachedConfig || {};
  const providerName = cfg?.provider || 'ocr_space';

  switch (providerName) {
    case 'ocr_space':
      return new OCRSpaceProvider({
        apiKey: cfg.api_key || '',
        timeout: cfg.timeout || 60000,
        language: cfg.language || 'spa',
      });

    // Future providers — add cases here:
    // case 'google_vision':
    //   return new GoogleVisionProvider({ apiKey: cfg.api_key, ... });
    // case 'azure_ocr':
    //   return new AzureOCRProvider({ apiKey: cfg.api_key, ... });
    // case 'aws_textract':
    //   return new AWSTextractProvider({ apiKey: cfg.api_key, ... });

    default:
      throw new Error(`Proveedor OCR desconocido: "${providerName}". Configura un proveedor válido en Configuración → IA → OCR.`);
  }
}

/**
 * Main OCR entry point.
 *
 * Extracts raw text from a file. Returns structured raw text fields.
 * NEVER interprets products — that is AIService's responsibility.
 *
 * @param {File|Blob} file
 * @param {string}    mimeType
 * @param {OCRConfig} [configOverride]
 * @returns {Promise<import('./interfaces/OCRProvider.js').OCRRawResult>}
 */
export async function extractTextFromFile(file, mimeType, configOverride) {
  // Load config if not cached
  if (!_cachedConfig && !configOverride) {
    await loadOCRConfig();
  }

  const config = configOverride || _cachedConfig || {};

  // FIX: No lanzar error si no hay api_key en frontend.
  // La API Key está en Supabase Secrets y la usa la Edge Function.
  // El frontend solo necesita la URL de Supabase (VITE_SUPABASE_URL).

  const provider = getActiveProvider(config);

  // Validate provider
  const validation = provider?.validate();
  if (!validation?.valid) {
    throw new Error(validation.error);
  }

  try {
    const result = await provider?.extractText(file, mimeType);
    return result;
  } catch (err) {
    // Re-throw with provider context
    const providerName = provider?.getProviderName();
    throw new Error(`[${providerName}] ${err?.message || 'Error desconocido al procesar el documento'}`);
  }
}

/**
 * Get list of available OCR providers for the configuration UI.
 * @returns {Array<{id: string, label: string, description: string}>}
 */
export function getAvailableProviders() {
  return [
    {
      id: 'ocr_space',
      label: 'OCR.Space',
      description: 'API REST de OCR. Soporta PDF e imágenes. Obtén tu API Key en ocr.space',
      apiKeyLabel: 'API Key de OCR.Space',
      apiKeyPlaceholder: 'helloworld (gratuita) o tu clave de pago',
      docsUrl: 'https://ocr.space/ocrapi',
    },
    // Future providers (disabled until implemented):
    {
      id: 'google_vision',
      label: 'Google Vision',
      description: 'Google Cloud Vision API. Alta precisión.',
      apiKeyLabel: 'Google Cloud API Key',
      apiKeyPlaceholder: 'AIza...',
      docsUrl: 'https://cloud.google.com/vision',
      comingSoon: true,
    },
    {
      id: 'azure_ocr',
      label: 'Azure OCR',
      description: 'Microsoft Azure Computer Vision.',
      apiKeyLabel: 'Azure Cognitive Services Key',
      apiKeyPlaceholder: 'Subscription Key',
      docsUrl: 'https://azure.microsoft.com/services/cognitive-services/computer-vision/',
      comingSoon: true,
    },
    {
      id: 'aws_textract',
      label: 'AWS Textract',
      description: 'Amazon Textract. Especializado en formularios y tablas.',
      apiKeyLabel: 'AWS Access Key',
      apiKeyPlaceholder: 'AKIA...',
      docsUrl: 'https://aws.amazon.com/textract/',
      comingSoon: true,
    },
  ];
}

export default { extractTextFromFile, loadOCRConfig, saveOCRConfig, getActiveProvider, getAvailableProviders };
